import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { EarnedCard } from '@/features/collection/earned-cards'

/**
 * The design's measured card, and the one knob that enlarges it — border and corner scale with it,
 * so a bigger card is not a thinner-framed one. Ported from CollectionEarnedCardsDeck.kt; the
 * numbers are shared with Android and iOS and should move together.
 */
const CARD_SCALE = 1.35
const CARD_WIDTH = 45.6 * CARD_SCALE
const CARD_HEIGHT = 60.8 * CARD_SCALE
const CARD_BORDER = 2.8 * CARD_SCALE
const CARD_RADIUS = 5.3 * CARD_SCALE

/** Degrees between neighbours. The fan is centred, so it stays symmetrical at any count. */
const SPREAD = 15

/** Clearance between the fan's lowest corner and the bar, and the dip a rotated card adds. */
const CLEARANCE = 26
const DIP = 10

/** INTO the bar rather than onto its edge, so the cards land on the tab they name. */
const LANDING = 16

const DEALING_MS = 420
const STAGGER_MS = 90
const HOLDING_MS = 1700
const GATHERING_MS = 450

/** How small a card has become by the time the tab takes it. */
const GATHERED = 0.18

/** The fan pivots away from the bar, so cards splay from a common corner. */
const PIVOT_OFFSET = 1.2

/** A damped spring's overshoot, as the one easing curve CSS can express. */
const DEAL_EASING = 'cubic-bezier(0.34, 1.4, 0.64, 1)'

export interface FanAnchor {
  /** Centre of the tab the cards gather into. */
  x: number
  /** The bar's inner edge — its top when the bar is at the bottom, its bottom when at the top. */
  y: number
  /** +1 when the bar is below the fan (mobile), -1 when above it (desktop's top nav). */
  dir: 1 | -1
}

/**
 * Where a card's centre appears given the pivot it scales about: `pivot + (p - pivot) * scale`.
 * One function, because the transform and the arithmetic undoing it must not disagree.
 */
function visibleCentreOf(pivotY: number, scale: number): number {
  const pivot = pivotY * CARD_HEIGHT
  return pivot + (CARD_HEIGHT / 2 - pivot) * scale
}

/**
 * Where a card's centre is meant to appear, measured from the top of the viewport.
 *
 * Exported because this is the whole mobile/desktop difference: `dir` mirrors the fan about the
 * bar, so the same numbers put it above a bottom nav and below a top one.
 */
export function cardCentreY(
  anchor: FanAnchor,
  out: number,
  away: number,
): number {
  const { y, dir } = anchor
  const base = y - dir * (CLEARANCE + DIP + CARD_HEIGHT / 2)
  // Rises 14 into the fan, then travels the whole way INTO the bar.
  const resting = base + dir * 14 * (1 - out)
  const landing = y + dir * LANDING
  return resting + (landing - resting) * away
}

function transformFor(
  index: number,
  count: number,
  anchor: FanAnchor,
  out: number,
  away: number,
): string {
  const { x, dir } = anchor
  const pivotY = 0.5 + dir * PIVOT_OFFSET

  // The fan is held the way a hand of cards is: every card sits on the same spot and pivots
  // about a point beyond itself, so they splay from a common corner.
  const angle = (index - (count - 1) / 2) * SPREAD
  // The hand closes as it is put away: the fan straightens on its way in, so three cards arrive
  // at the tab as one stack rather than three splayed ones.
  const rotation = angle * out * (1 - away)
  const size = (0.55 + 0.45 * out) * (1 - away) + GATHERED * away

  const translateX = x - CARD_WIDTH / 2
  const translateY =
    cardCentreY(anchor, out, away) - visibleCentreOf(pivotY, size)

  return `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg) scale(${size})`
}

function opacityFor(out: number, away: number): number {
  // Late and short, inside the travel — the card is still solid when it lands.
  const fade = Math.min(Math.max((away - 0.51) / 0.49, 0), 1)
  return out * (1 - fade)
}

type Phase = 'dealing' | 'holding' | 'gathering'

/**
 * What the practice just earned, fanned over the app and gathered into the Collection tab.
 *
 * React rather than the shared Compose deck because it must draw the moment a practice ends,
 * anywhere in the app — the wasm bundle only exists while /collection is mounted, and waiting on
 * 22MB would miss the moment this exists for. Same reasoning as the tab's dot.
 */
export function EarnedCardsFan({
  cards,
  anchor,
  onFinished,
}: {
  cards: EarnedCard[]
  anchor: FanAnchor
  onFinished: () => void
}) {
  const [phase, setPhase] = useState<Phase>('dealing')
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

  useEffect(() => {
    const hold = (cards.length - 1) * STAGGER_MS + HOLDING_MS
    // One frame before leaving the dealt-from state, or the browser has nothing to animate from.
    const dealt = requestAnimationFrame(() => setPhase('holding'))
    if (reduceMotion) {
      const done = setTimeout(onFinished, hold)
      return () => {
        cancelAnimationFrame(dealt)
        clearTimeout(done)
      }
    }
    const gather = setTimeout(() => setPhase('gathering'), DEALING_MS + hold)
    // The whole travel, not just the fade — cleared once the cards have landed.
    const done = setTimeout(onFinished, DEALING_MS + hold + GATHERING_MS)
    return () => {
      cancelAnimationFrame(dealt)
      clearTimeout(gather)
      clearTimeout(done)
    }
  }, [cards, onFinished, reduceMotion])

  const out = phase === 'dealing' ? 0 : 1
  const away = phase === 'gathering' ? 1 : 0
  const duration = phase === 'gathering' ? GATHERING_MS : DEALING_MS
  const easing =
    phase === 'gathering' ? 'cubic-bezier(0.42, 0, 1, 1)' : DEAL_EASING

  return createPortal(
    <div
      aria-hidden
      // Over the nav it gathers into, and never in the way of it.
      className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
    >
      {cards.map((card, index) => (
        <div
          key={card.id}
          className="absolute top-0 left-0 overflow-hidden bg-app-grey-200"
          style={{
            width: CARD_WIDTH,
            height: CARD_HEIGHT,
            borderRadius: CARD_RADIUS,
            border: `${CARD_BORDER}px solid #fff`,
            transformOrigin: `50% ${(0.5 + anchor.dir * PIVOT_OFFSET) * 100}%`,
            transform: transformFor(index, cards.length, anchor, out, away),
            opacity:
              reduceMotion && phase !== 'gathering'
                ? out
                : opacityFor(out, away),
            transition: reduceMotion
              ? undefined
              : `transform ${duration}ms ${easing}, opacity ${duration}ms ${easing}`,
          }}
        >
          {card.thumbnailUrl && (
            <img
              src={card.thumbnailUrl}
              alt=""
              className="h-full w-full object-cover"
              draggable={false}
            />
          )}
        </div>
      ))}
    </div>,
    document.body,
  )
}
